import { HttpClient, HttpHeaders, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import {
  EtapaV2,
  GetAllPlanosRequest,
  GetBNCCRequest,
  GetLKPDisciplinasRequest,
  GetLKPIntervaloRequest,
  GetLKPPeriodoRequest,
  GetLKPTurmaRequest,
  GetPlanosExcluidosRequest,
  GetPlanoAulaOutraEscolaRequest,
  GetPlanoAulaPorIdRequest,
  ListaPlanoAulaOutraEscola,
  PlanoAulaOutraEscola,
  GetTitulosRequest,
  GetPlanoAulaPeriodoTurmaRequest,
  ListaLKPAtividades,
  ListaLKPDisciplinas,
  ListaLKPIntervalo,
  ListaLKPPeriodo,
  ListaLKPPlanoAulaRecurso,
  ListaLKPTurma,
  ListaEtapaEF,
  ListaEtapaEI,
  ListaEtapaEM,
  ListaEtapaDiv,
  ListaPlanoAulaAtividade,
  ListaPlanoAulaPorId,
  ListaPlanoAulaRecurso,
  ListaPlanoDeAulaDisciplina,
  ListaPesquisaBNCC,
  ListaTituloPlanoAula,
  PlanoAulaPeriodoTurma,
} from '../models';
import { PLANO_AULA_API_URL } from './plano-aula-api.config';

/**
 * Serviço responsável por carregar dados de plano de aula
 * Centraliza todas as chamadas HTTP de leitura
 */
@Injectable({
  providedIn: 'root',
})
export class PlanoAulaLoadService {
  private readonly http = inject(HttpClient);
  private readonly apiUrl = inject(PLANO_AULA_API_URL);

  /**
   * Busca plano de aula por ID
   */
  getPlanoAulaPorId(
    request: GetPlanoAulaPorIdRequest,
  ): Observable<ListaPlanoAulaPorId> {
    const params = new HttpParams()
      .append('TIPO', 'PLANO_AULA')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('DATABASE', request.DATABASE);

    return this.http.get<ListaPlanoAulaPorId>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca títulos de configuração do plano de aula por etapa BNCC
   */
  getTitulos(request: GetTitulosRequest): Observable<ListaTituloPlanoAula> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_CONFIG')
      .append('ETAPA_BNCC', request.ETAPA_BNCC)
      .append('DATABASE', request.DATABASE);

    return this.http.get<ListaTituloPlanoAula>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca períodos (bimestres) disponíveis para uma turma
   */
  getPeriodo(request: GetLKPPeriodoRequest): Observable<ListaLKPPeriodo> {
    return this.http.get<ListaLKPPeriodo>(`${this.apiUrl}/planoaula`, { params: {
        TIPO: 'LKP_PERIODO',
        CDESCOLA: request.CDESCOLA,
        ID_TURMA: request.ID_TURMA,
        ANO: request.ANO,
        DATABASE: request.DATABASE,
      },
    });
  }

  /**
   * Busca disciplinas LKP disponíveis para uma turma e bimestre
   */
  getLKPDisciplinas(
    request: GetLKPDisciplinasRequest,
  ): Observable<ListaLKPDisciplinas> {
    return this.http.get<ListaLKPDisciplinas>(`${this.apiUrl}/planoaula`, { params: {
        TIPO: 'LKP_DISCIPLINAS',
        ID_TURMA: request.ID_TURMA,
        BIMESTRE: request.BIMESTRE,
        ANO: request.ANO,
        CDESCOLA: request.CDESCOLA,
        CPF: request.CPF,
        DATABASE: request.DATABASE,
      },
    });
  }

  /**
   * Busca turmas LKP disponíveis
   */
  getLKPTurma(request: GetLKPTurmaRequest): Observable<ListaLKPTurma> {
    let params = new HttpParams()
      .append('TIPO', 'LKP_TURMA')
      .append('ANO', request.ANO)
      .append('CDESCOLA', request.CDESCOLA)
      .append('CPF', request.CPF)
      .append('PERIODO', request.PERIODO)
      .append('INCLUSAO', request.INCLUSAO)
      .append('DATABASE', request.DATABASE);

    if (request.CPF_COORDENADOR) {
      params = params.append('CPF_COORDENADOR', request.CPF_COORDENADOR);
    }
    if (request.CDSERIE != null) {
      params = params.append('CDSERIE', request.CDSERIE);
    }
    if (request.EXERC != null) {
      params = params.append('EXERC', request.EXERC);
    }

    return this.http.get<ListaLKPTurma>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca intervalo de avaliação LKP
   */
  getLKPIntervalo(
    request: GetLKPIntervaloRequest,
  ): Observable<ListaLKPIntervalo> {
    const params = new HttpParams()
      .append('TIPO', 'LKP_INTERVALO')
      .append('INTERVALOAVALIACAO', request.INTERVALOAVALIACAO)
      .append('DATABASE', request.DATABASE);

    return this.http.get<ListaLKPIntervalo>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca disciplinas vinculadas a um plano de aula
   */
  getDisciplinas(
    idPlanoAula: number,
    database: string,
  ): Observable<ListaPlanoDeAulaDisciplina> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_DISCIPLINAS')
      .append('ID_PLANO_AULA', idPlanoAula)
      .append('DATABASE', database);

    return this.http.get<ListaPlanoDeAulaDisciplina>(
      `${this.apiUrl}/planoaula`,
      { params,
      },
    );
  }

  /**
   * Busca atividades vinculadas a um plano de aula
   */
  getAtividades(
    idPlanoAula: number,
    database: string,
  ): Observable<ListaPlanoAulaAtividade> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ATIVIDADE')
      .append('ID_PLANO_AULA', idPlanoAula)
      .append('DATABASE', database);

    return this.http.get<ListaPlanoAulaAtividade>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca recursos vinculados a um plano de aula
   */
  getRecursos(
    idPlanoAula: number,
    database: string,
  ): Observable<ListaPlanoAulaRecurso> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_RECURSO')
      .append('ID_PLANO_AULA', idPlanoAula)
      .append('DATABASE', database);

    return this.http.get<ListaPlanoAulaRecurso>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Consulta planos de aula com filtros
   */
  getAll(request: GetAllPlanosRequest): Observable<any[]> {
    let params = new HttpParams()
      .append('tipo', 'CONSULTA_PLANO_AULA')
      .append('DATABASE', request.DATABASE)
      .append('CDESCOLA', request.CDESCOLA)
      .append('ANO', request.ANO);

    if (request.CPF) params = params.append('CPF', request.CPF);
    if (request.CPF_COORDENADOR)
      params = params.append('CPF_COORDENADOR', request.CPF_COORDENADOR);
    if (request.CDDISC) params = params.append('CDDISC', request.CDDISC);
    if (request.BIMESTRE) params = params.append('BIMESTRE', request.BIMESTRE);
    if (request.ID_TURMA)
      params = params.append('ID_TURMA', request.ID_TURMA);
    if (request.DATAINICIO)
      params = params.append('DATAINICIO', request.DATAINICIO);
    if (request.DATAFINAL)
      params = params.append('DATAFINAL', request.DATAFINAL);
    if (request.ID_PLANO_AULA)
      params = params.append('ID_PLANO_AULA', request.ID_PLANO_AULA);

    return this.http
      .get<{ PLANOAULA: any[] }>(`${this.apiUrl}/planoaula`, { params,
      })
      .pipe(map((res) => res.PLANOAULA ?? []));
  }

  /**
   * Busca planos de aula excluídos
   */
  getPlanosExcluidos(
    request: GetPlanosExcluidosRequest,
  ): Observable<any[]> {
    const params = new HttpParams()
      .append('TIPO', 'PLANO_AULA_EXCLUIDOS')
      .append('CPF', request.CPF)
      .append('CDESCOLA', request.CDESCOLA)
      .append('ANO', request.ANO)
      .append('DATABASE', request.DATABASE);

    return this.http.get<any[]>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Lista planos de aula do professor em outras escolas do município.
   */
  getPlanosOutraEscola(
    request: GetPlanoAulaOutraEscolaRequest,
  ): Observable<ListaPlanoAulaOutraEscola> {
    const params = new HttpParams()
      .append('TIPO', 'PLANO_AULA_OUTRA_ESCOLA')
      .append('CPF', request.CPF)
      .append('CDESCOLA', request.CDESCOLA)
      .append('ANO', request.ANO)
      .append('BIMESTRE', request.BIMESTRE)
      .append('DATABASE', request.DATABASE);

    return this.http
      .get<ListaPlanoAulaOutraEscola | PlanoAulaOutraEscola>(
        `${this.apiUrl}/planoaula`,
        { params },
      )
      .pipe(map((res) => normalizarListaPlanoOutraEscola(res)));
  }

  /**
   * Busca catálogo de atividades disponíveis (LKP)
   */
  getLKP_ATIVIDADES(request: {
    DATABASE: string;
  }): Observable<ListaLKPAtividades> {
    const params = new HttpParams()
      .append('tipo', 'LKP_ATIVIDADES')
      .append('DATABASE', request.DATABASE);

    return this.http.get<ListaLKPAtividades>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca catálogo de recursos disponíveis (LKP)
   */
  getLKP_PLANO_AULA_RECURSO(request: {
    TIPO_RECURSO?: string;
    DATABASE: string;
    CPF?: string;
    RECURSO?: string;
  }): Observable<ListaLKPPlanoAulaRecurso> {
    const params = new HttpParams()
      .append('tipo', 'LKP_PLANO_AULA_RECURSO')
      .append('CPF', request.CPF ?? '')
      .append('TIPO_RECURSO', request.TIPO_RECURSO ?? '')
      .append('DATABASE', request.DATABASE)
      .append('RECURSO', request.RECURSO ?? '');

    return this.http.get<ListaLKPPlanoAulaRecurso>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca BNCC (Base Nacional Comum Curricular) para uma disciplina
   */
  getBNCC(request: GetBNCCRequest): Observable<ListaPesquisaBNCC> {
    const params = new HttpParams()
      .append('tipo', 'PESQUISABNCCV2')
      .append('CDESCOLA', request.planoDeAula.CDESCOLA)
      .append('ANO', request.planoDeAula.EXERC)
      .append('BIMESTRE', request.planoDeAula.BIMESTRE)
      .append('CPF', request.planoDeAula.CPF)
      .append('ID_TURMA', request.planoDeAula.ID_TURMA)
      .append('CDDISC', request.disciplina.CDDISC)
      .append('ETAPA_BNCC', request.disciplina.ETAPA_BNCC)
      .append('IDAREACOMPONENTE', request.disciplina.IDAREACOMPONENTE)
      .append('DATABASE', request.DATABASE)
      .append('FILTRO', request.FILTRO ?? '')
      .append('VALOR', request.VALOR ?? '');

    return this.http.get<ListaPesquisaBNCC>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca etapa BNCC V2 (habilidades/objetos de conhecimento)
   */
  getEtapaV2(request: {
    ID_PLANO_AULA: number;
    ETAPA_BNCC: number;
    DATABASE: string;
    CDDISC: number;
    IDAREACOMPONENTE: number;
  }): Observable<EtapaV2> {
    const params = new HttpParams()
      .append('TIPO', 'PLANO_AULA_ETAPAV2')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('ETAPA_BNCC', request.ETAPA_BNCC)
      .append('DATABASE', request.DATABASE)
      .append('CDDISC', request.CDDISC)
      .append('IDAREACOMPONENTE', request.IDAREACOMPONENTE);

    return this.http.get<EtapaV2>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca etapas BNCC Educação Infantil (ETAPA = 1)
   */
  getEtapaEI(request: {
    ID_PLANO_AULA: number;
    DATABASE: string;
  }): Observable<ListaEtapaEI> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ETAPA_EI')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('DATABASE', request.DATABASE);

    return this.http.get<ListaEtapaEI>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca etapas BNCC Ensino Fundamental (ETAPA = 2)
   */
  getEtapaEF(request: {
    ID_PLANO_AULA: number;
    DATABASE: string;
  }): Observable<ListaEtapaEF> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ETAPA_EF')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('DATABASE', request.DATABASE);

    return this.http.get<ListaEtapaEF>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca etapas BNCC Ensino Médio (ETAPA = 3)
   */
  getEtapaEM(request: {
    ID_PLANO_AULA: number;
    DATABASE: string;
  }): Observable<ListaEtapaEM> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ETAPA_EM')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('DATABASE', request.DATABASE);

    return this.http.get<ListaEtapaEM>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca etapas BNCC Parte Diversificada (ETAPA = 4)
   */
  getEtapaDIV(request: {
    ID_PLANO_AULA: number;
    DATABASE: string;
  }): Observable<ListaEtapaDiv> {
    const params = new HttpParams()
      .append('tipo', 'PLANO_AULA_ETAPA_DIV')
      .append('ID_PLANO_AULA', request.ID_PLANO_AULA)
      .append('DATABASE', request.DATABASE);

    return this.http.get<ListaEtapaDiv>(`${this.apiUrl}/planoaula`, { params,
    });
  }

  /**
   * Busca o intervalo de datas com plano de aula da turma no período.
   */
  getPlanoAulaPeriodoTurma(
    request: GetPlanoAulaPeriodoTurmaRequest,
  ): Observable<PlanoAulaPeriodoTurma> {
    const params = new HttpParams()
      .append('TIPO', 'PLANO_AULA_PERIODO_TURMA')
      .append('CDESCOLA', request.CDESCOLA)
      .append('ID_TURMA', request.ID_TURMA)
      .append('CPF', request.CPF)
      .append('BIMESTRE', request.BIMESTRE)
      .append('DATABASE', request.DATABASE);

    return this.http
      .get<PlanoAulaPeriodoTurma[] | PlanoAulaPeriodoTurma>(
        `${this.apiUrl}/planoaula`,
        { params },
      )
      .pipe(map((res) => (Array.isArray(res) ? res[0] : res)));
  }
}

function normalizarListaPlanoOutraEscola(
  res: ListaPlanoAulaOutraEscola | PlanoAulaOutraEscola | null | undefined,
): ListaPlanoAulaOutraEscola {
  if (Array.isArray(res)) {
    return res.filter((item) => item?.ID_PLANO_AULA != null);
  }
  if (res && res.ID_PLANO_AULA != null) {
    return [res];
  }
  return [];
}
